import {AxiosError} from "axios";
import {Plus as AddIcon, Xmark as RemoveIcon} from "iconoir-react";
import {useEffect, useState} from 'react'
import {Collection as GroupIcon} from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {DLPApi, DLPPolicyPattern} from "../api/admin/generated";
import {DialogCloseCause} from "../common/Enums";
import {Pager} from "../common/Pager";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";
import './DLPGroupDialog.scss';
import {DLPColumn} from "./DLPColumn";
import {DLPTableHeader} from "./DLPTableHeader";

/*
 * REST api for accessing the back-end
 */
const dlpApi = new DLPApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface DLPGroupDialogProps {
    editName? : string
    onClose : OnClose
}

export const DLPGroupDialog = ({editName, onClose} : DLPGroupDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Pattern name
     */
    const [name, setName] = useState<string>(editName??"");
    /**
     * Children
     */
    const [childPatternNames, setChildPatternNames] = useState<string[]>([]);
    /**
     * Children
     */
    const [childPatterns, setChildPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * All patterns
     */
    const [patterns, setPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The total number of patterns
     */
    const [patternsCount, setPatternsCount] = useState<number>(0);
    /**
     * The selected child patterns
     */
    const [selectedChildPatterns, setSelectedChildPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The selected patterns
     */
    const [selectedPatterns, setSelectedPatterns] = useState<DLPPolicyPattern[]>([]);
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(5);
    /**
     * Set to true while adding the pattern to show a spinner on the import button
     */
    const [saving, setSaving] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        // only load child pattern names if editing a pattern
        if (editName === undefined) {
            return
        }

        logger.debug("Load child pattern names")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPattern(editName)
            .then(response => {
                setChildPatternNames(response.data.childNodeNames);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error child pattern names", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        editName,
        t
    ]);

    useEffect(() =>
    {
        // only load child patterns if editing a pattern
        if (editName === undefined || childPatternNames.length === 0) {
            return
        }

        logger.debug("Load child patterns")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPatternsByName(childPatternNames)
            .then(response => {
                setChildPatterns(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error loading child patterns", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        editName,
        childPatternNames,
        t]);

    useEffect(() =>
    {
        logger.debug("Load pattern count")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPatternCount()
            .then(response => {
                setPatternsCount(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting pattern count", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load patterns")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPatterns((currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setPatterns(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting patterns", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const saveChanges = () =>
    {
        if (editName === undefined)
        {
            // the group pattern must be added before child patterns can be added
            setSaving(true);

            dlpApi.dLPControllerAddPolicyGroup(name).then(() => {
                saveChildPatterns();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error saving changes", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setSaving(false);
            });
        }
        else {
            saveChildPatterns();
        }
    }

    const saveChildPatterns = () =>
    {
        setSaving(true);

        dlpApi.dLPControllerSetChildNodes(name, childPatterns.map(p => p.name)).then(() => {
            onClose(DialogCloseCause.Succes);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error saving child patterns", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSaving(false);
        });
    }

    const onChangeSelectChildPatterns = (checked : boolean, pattern : DLPPolicyPattern) =>
    {
        setSelectedChildPatterns(checked ?
            [...selectedChildPatterns, pattern] :
            selectedChildPatterns.filter(p => p.name !== pattern.name)
        );
    }

    const onChangeSelectPatterns = (checked : boolean, pattern : DLPPolicyPattern) =>
    {
        setSelectedPatterns(checked ?
            [...selectedPatterns, pattern] :
            selectedPatterns.filter(p => p.name !== pattern.name)
        );
    }

    /**
     * An available pattern should be disabled if it is already a member of the child patterns or if the group
     * is being edited
     */
    function isAvailablePatternDisabled(name : string) {
        return childPatterns.map(chp => chp.name).includes(name) || name === editName
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{editName === undefined ? t("dlp.add-group") : t("dlp.edit-group")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>
                        <Form>
                            <FloatingLabel label={t("dlp.name")} className="text-secondary mb-3">
                                <Form.Control type="text" placeholder={t("dlp.name")} value={name}
                                              onChange={e => setName(e.target.value)}
                                              disabled={editName !== undefined}/>
                            </FloatingLabel>
                        </Form>

                        <div className="fs-5 text-muted mb-2">{t("dlp.group-patterns")}</div>

                        <Table className="caption-top" bordered hover responsive>
                            {selectedChildPatterns.length === 0 ?
                                <DLPTableHeader/>
                                :
                                <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    <th colSpan={9}>
                                        <Navbar className="bg-body-tertiary ps-2">
                                            <Navbar.Text className="ms-2">{selectedChildPatterns.length} selected</Navbar.Text>

                                            <div className="vr ms-4 me-2"></div>

                                            <Nav>
                                                <Nav.Link onClick={() => {
                                                    setChildPatterns([...childPatterns].filter(p => !selectedChildPatterns.includes(p)));
                                                    setSelectedChildPatterns([])
                                                }}>
                                                    <RemoveIcon className="text-danger"/> {t("dlp.remove-selected")}
                                                </Nav.Link>
                                            </Nav>
                                        </Navbar>
                                    </th>
                                </tr>
                                </thead>
                            }
                            <tbody>
                            {childPatterns?.map((p: DLPPolicyPattern) => (
                                <tr key={p.name} className="text-nowrap">
                                    <DLPColumn className="td-w-min">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={selectedChildPatterns.includes(p)}
                                            onChange={(e) => onChangeSelectChildPatterns(e.target.checked, p)}
                                            inline
                                        />
                                        {p.group && <GroupIcon className="ms-2" title={t("dlp.group")}/>}
                                    </DLPColumn>
                                    <DLPColumn value={p.name}/>
                                    <DLPColumn value={p.description}/>
                                    <DLPColumn value={p.regEx}/>
                                    <DLPColumn value={p.validator}/>
                                    <DLPColumn value={p.group ? "" : `${p.threshold}`}/>
                                    <DLPColumn value={p.action}/>
                                    <DLPColumn value={p.group ? "" : `${p.delayEvaluation}`}/>
                                    <DLPColumn value={p.matchFilter}/>
                                </tr>
                            ))}
                            </tbody>
                        </Table>

                        <Navbar className="bg-body-tertiary">
                            <Pager itemCount={patternsCount}
                                   currentPage={currentPage}
                                   setCurrentPage={setCurrentPage}
                                   maxItemsPerPage={rowsPerPage}
                            />

                            <Nav className="ms-auto">
                                <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                             setCurrentPage={setCurrentPage}/>
                            </Nav>
                        </Navbar>

                        <div className="fs-5 text-muted mb-2">{t("dlp.available-patterns")}</div>

                        <Table className="caption-top" bordered hover responsive>
                            {selectedPatterns.length === 0 ?
                                <DLPTableHeader caption={t("dlp.available-patterns")}/>
                                :
                                <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    <th colSpan={9}>
                                        <Navbar className="bg-body-tertiary ps-2">
                                            <Navbar.Text className="ms-2">{selectedPatterns.length} selected</Navbar.Text>

                                            <div className="vr ms-4 me-2"></div>

                                            <Nav>
                                                <Nav.Link onClick={() => {
                                                    setChildPatterns([...childPatterns, ...selectedPatterns]);
                                                    setSelectedPatterns([])
                                                }}>
                                                    <AddIcon className="text-success"/> {t("dlp.add-selected")}
                                                </Nav.Link>
                                            </Nav>
                                        </Navbar>
                                    </th>
                                </tr>
                                </thead>
                            }
                            <tbody>
                            {patterns.map((p: DLPPolicyPattern) => (
                                <tr key={p.name}
                                    className={"text-nowrap" + (isAvailablePatternDisabled(p.name) ? " table-secondary" : "")}>
                                    <DLPColumn className="td-w-min">
                                        <Form.Check
                                            type="checkbox"
                                            className="me-0"
                                            checked={selectedPatterns.includes(p)}
                                            onChange={(e) => onChangeSelectPatterns(e.target.checked, p)}
                                            disabled={isAvailablePatternDisabled(p.name)}
                                            inline
                                        />
                                        {p.group && <GroupIcon className="ms-2" title={t("dlp.group")}/>}
                                    </DLPColumn>
                                    <DLPColumn value={p.name}/>
                                    <DLPColumn value={p.description}/>
                                    <DLPColumn value={p.regEx}/>
                                    <DLPColumn value={p.validator}/>
                                    <DLPColumn value={p.group ? "" : `${p.threshold}`}/>
                                    <DLPColumn value={p.action}/>
                                    <DLPColumn value={p.group ? "" : `${p.delayEvaluation}`}/>
                                    <DLPColumn value={p.matchFilter}/>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={saveChanges}
                        disabled={StringUtils.isBlank(name)}>
                    {saving && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {saving ? t("common.saving") : t("common.save")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
